import { useCallback, useState } from "react";
import GoBack from "../../components/GoBack";
import Header from "../../components/Header";
import { useDropzone } from "react-dropzone";
import {
  AddCircleOutline,
  AddPhotoAlternateOutlined,
  Close,
  ExpandMore,
} from "@mui/icons-material";
import ModalComponent from "../../components/Modals/modal";
import { useNavigate, useParams } from "react-router-dom";
import CropCategoryOne from "../../containers/catalogue/cropCategoryOne";
import CropCategoryTwo from "../../containers/catalogue/cropCategoryTwo";
import CropCategoryThree from "../../containers/catalogue/cropCategoryThree";
import CropCategoryFour from "../../containers/catalogue/cropCategoryFour";
import CropCategoryFive from "../../containers/catalogue/cropCategoryFive";
import Variant from "../../containers/catalogue/variants/variant";
import toast from "react-hot-toast";
import Color from "../../containers/catalogue/variants/color";
import { useDispatch, useSelector } from "react-redux";
import { addImages, createProdut } from "../../redux/Catalog/catalogActions";

const AddProduct = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const [files, setFiles] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(null);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState({});
  const [productName, setProductName] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const { selectedCategory } = useSelector((state) => state.catalog);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState({
    colour: {
      values: [""],
      custom: false,
    },
    // Dynamic custom variants will be added as new keys, e.g. material, size, etc.
  });

  const status = [
    {
      id: 1,
      name: "active",
    },
    {
      id: 2,
      name: "inactive",
    },
  ];

  const [categoryOneData, setCategoryOneData] = useState({
    vendor: "",
    product_class: "",
    dose_rate: "",
    time_of_application: "",
    target_crops: "",
    mixing_and_application: "",
    active_ingredient: "",
  });
  const [categoryTwoData, setCategoryTwoData] = useState({
    vendor: "",
    product_class: "",
    dose_rate: "",
    target_crops: [],
    mixing_and_application: "",
    active_ingredient: "",
  });

  const [categoryThreeData, setCategoryThreeData] = useState({
    vendor: "",
    dose_rate: "",
    use_rate: "",
    composition: "",
    target_crops: "",
    mode_of_action: "",
  });

  const [categoryFourData, setCategoryFourData] = useState({
    vendor: "",
    dose_rate: "",
    target_crops: "",
    mixing_and_application: "",
    active_ingredient: "",
  });

  const [categoryFiveData, setCategoryFiveData] = useState({
    vendor: "",
    seed_rate: "",
    adaptability: "",
    requirements: "",
  });

  const fileWithPreviews = files.map((file) =>
    typeof file === "string"
      ? file
      : Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const filteredFiles = acceptedFiles.filter((f) => {
        const isImage =
          f.type === "image/jpeg" ||
          f.type === "image/png" ||
          f.type === "image/jpg";
        const isKbSize = f.size >= 1024 && f.size <= 1024 * 1024; // 1KB to 1MB
        if (!isKbSize) {
          toast.error(
            `${f.name} is not within the allowed size (1KB - 1024KB)`
          );
        }
        return isImage && isKbSize;
      });
      setFiles((prevFiles) => [...prevFiles, ...filteredFiles]);
    },
    [setFiles]
  );

  const handleRemoveFile = (fileToRemove, removeIndex) => {
    setFiles((prevFiles) => prevFiles.filter((f, i) => i !== removeIndex));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/jpg": [],
    },
    multiple: true,
  });

  const handleSelectedStatus = (status) => {
    setIsStatusOpen(false);
    setSelectedStatus(status);
  };

  const isAnyFieldEmpty = (obj) => Object.values(obj).some((value) => !value);

  const active = () => {
    if (
      !productName ||
      !productDescription ||
      files.length === 0 ||
      selected.length === 0 ||
      !selectedStatus.name
    ) {
      return true;
    }

    if (
      [
        "Bird Repellant",
        "Storage Product",
        "Rodenticide",
        "Nematicide",
      ].includes(type)
    ) {
      return isAnyFieldEmpty(categoryTwoData);
    }
    if (
      [
        "Herbicide",
        "Insecticide",
        "Plant Growth Regulator Solution",
        "Fungicide",
      ].includes(type)
    ) {
      return isAnyFieldEmpty(categoryOneData);
    }
    if (type === "Fertilizers") {
      return isAnyFieldEmpty(categoryThreeData);
    }
    if (type === "Seed Dressing") {
      return isAnyFieldEmpty(categoryFourData);
    }
    if (type === "Seed") {
      return isAnyFieldEmpty(categoryFiveData);
    }

    return false;
  };

  // Returns the correct category data object based on the product type
  const getCategoryData = () => {
    if (
      [
        "Bird Repellant",
        "Storage Product",
        "Rodenticide",
        "Nematicide",
      ].includes(type)
    ) {
      return { ...categoryTwoData, vendor: categoryTwoData.vendor.id };
    }
    if (
      [
        "Herbicide",
        "Insecticide",
        "Plant Growth Regulator Solution",
        "Fungicide",
      ].includes(type)
    ) {
      return { ...categoryOneData, vendor: categoryOneData.vendor.id };
    }
    if (type === "Fertilizers") {
      return {
        vendor: categoryThreeData.vendor.id,
        dose_rate: categoryThreeData.dose_rate,
        use_rate: categoryThreeData.use_rate,
        composition: categoryThreeData.composition,
        target_crops: categoryThreeData.target_crops.map((data) => data.id),
        mode_of_action: categoryThreeData.mode_of_action,
      };
    }
    if (type === "Seed Dressing") {
      return { ...categoryFourData, vendor: categoryFourData.vendor.id };
    }
    if (type === "Seed") {
      return { ...categoryFiveData, vendor: categoryFiveData.vendor.id };
    }
    return null;
  };

  const handleSaveChanges = async (data) => {
    setLoading(true);

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("images", file);
    });

    // Convert files to cloudinary URLs before sending
    dispatch(addImages({ data: formData })).then((res) => {
      if (res.type === "addImages/fulfilled") {
        const payload = {
          product_name: productName,
          product_description: productDescription,
          is_visible: selectedStatus.name === "active" ? true : false,
          product_type: selectedCategory.type,
          product_category: selectedCategory.id,
          product_details: getCategoryData(),
          variants: selected,
          images: res?.payload?.data?.images,
        };
        dispatch(createProdut({ payload })).then((res) => {
          if (res?.type === "createProdut/fulfilled") {
            if (data === "not-now") {
              setIsOpen(false);
              toast.success("Product saved successfully");
              navigate("/product-inventory");
            } else {
              setIsOpen(false);
              toast.success("Product added to inventory successfully");
              navigate(
                `/product-inventory/add-inventory?product_id=${res.payload.data.id}`
              );
            }
            setLoading(false);
          } else {
            setIsOpen(false);
            if (res?.payload?.error?.product_name) {
              toast.error(res?.payload?.error?.product_name[0]);
            } else if (res?.payload?.message) {
              toast.error(res?.payload?.message);
            } else {
              toast.error("An error occurred while creating this product.");
            }

            setLoading(false);
          }
        });
      }
    });
  };

  return (
    <div>
      <GoBack />

      <div className="mt-3">
        <Header
          containerStyle="border border-[#E1E6E1] py-3"
          style={{ color: "#2E332F" }}
          text={`Add Product (${type})`}
        >
          <div className="flex gap-6 items-center">
            <p className="text-[#5C715E] font-medium cursor-pointer text-sm leading-5 px-3">
              Discard
            </p>
            <button
              disabled={active() || loading}
              onClick={() => setIsOpen(true)}
              className="outline-none cursor-pointer bg-primary border disabled:bg-[#ABB6AC] disabled:border-[#ABB6AC] border-primary text-white font-medium text-sm leading-5 py-[10px] px-[20px] rounded-[8px]  hover:border hover:border-primary  transition ease-in-out delay-100"
            >
              Save Changes
            </button>
          </div>
        </Header>
      </div>

      <div className="flex gap-2 mt-3">
        {/* FIRST SECTION */}
        <div className="w-[60%]">
          <div className="rounded-lg bg-white p-4 h-fit border border-[#E1E6E1]">
            {/* PRODUCT NAME */}
            <div className="flex flex-col gap-4">
              <div>
                <div className="flex items-center mb-1">
                  <p className="text-[#344335] font-normal text-sm leading-[18px]">
                    Product Name
                  </p>
                </div>
                <input
                  type="text"
                  onChange={(event) => setProductName(event.target.value)}
                  placeholder="Product Name"
                  className="outline-none border border-[#96A397] w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] text-sm font-medium leading-5"
                />
              </div>

              <div>
                <div className="flex items-center mb-1">
                  <p className="text-[#344335] font-normal text-sm leading-[18px]">
                    Product Description
                  </p>
                </div>

                <textarea
                  onChange={(event) =>
                    setProductDescription(event.target.value)
                  }
                  type="text"
                  placeholder="Product Description"
                  className="outline-none border border-[#96A397] resize-none w-full h-[134px] rounded-lg px-3 py-4 placeholder:text-[#96A397] text-sm font-medium leading-5"
                />
              </div>
            </div>
          </div>
          {/* PRODUCT IMAGES */}
          <div className="rounded-lg bg-white h-fit border border-[#E1E6E1] mt-3">
            <div className="flex flex-col gap-4 mb-3">
              <div className="border-b border-b-[#E1E6E1] px-4 py-3 pb-3">
                <div className="flex items-center mb-1">
                  <p className="text-[#344335] font-normal text-sm leading-[18px]">
                    Product Images
                  </p>
                </div>
              </div>
              <div className="px-4">
                <div>
                  {isDragActive ? (
                    <div className="flex gap-3 flex-wrap items-center justify-center px-3 py-3 cursor-pointer border border-[#E1E6E1] w-full h-[146px] rounded-lg">
                      <p className="text-[#16461A] font-medium text-sm text-center">
                        Drop the files here ...
                      </p>
                    </div>
                  ) : (
                    <div>
                      {files.length !== 0 ? (
                        <div className="flex gap-3 flex-wrap px-3 py-3 cursor-pointer border border-[#E1E6E1] w-full h-full min-h-[146px] rounded-lg">
                          {fileWithPreviews.map((filedata, index) => (
                            <div
                              key={index}
                              onMouseLeave={() => setImageIndex(null)}
                              onMouseEnter={() => setImageIndex(index)}
                              className="relative"
                            >
                              {imageIndex === index && (
                                <div className="absolute border border-[#E1E6E1] rounded-full w-5 h-5 flex justify-center items-center top-[-5px] right-[-3px] bg-white z-10">
                                  <Close
                                    onClick={() =>
                                      handleRemoveFile(filedata, index)
                                    }
                                    style={{
                                      color: "#4B564D",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </div>
                              )}
                              <div className="w-[48px] h-[48px] rounded-lg border border-[#E1E6E1] overflow-hidden bg-white">
                                <img
                                  src={filedata.preview || filedata}
                                  alt="preview"
                                  className="object-cover w-full h-full rounded-lg"
                                />
                              </div>
                            </div>
                          ))}
                          {/* Add more images */}
                          <div
                            {...getRootProps()}
                            className="w-[48px] h-[48px] rounded-lg border border-[#E1E6E1] bg-[#F6F7F6] flex justify-center items-center"
                            style={{ cursor: "pointer" }}
                          >
                            <AddPhotoAlternateOutlined
                              style={{ fontSize: "24px", color: "#2B8C34" }}
                            />
                            <input {...getInputProps()} />
                          </div>
                        </div>
                      ) : (
                        <div
                          {...getRootProps()}
                          className="flex justify-center items-center w-full h-[146px] border border-[#E1E6E1] rounded-lg"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="flex flex-col justify-center items-center">
                            <AddPhotoAlternateOutlined
                              style={{ fontSize: "24px", color: "#2B8C34" }}
                            />
                            <p className="text-[#16461A] font-medium text-sm max-w-[140px] text-center mt-2">
                              Upload a .jpeg or .png file of the product
                            </p>
                            <input {...getInputProps()} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* VARIANT SECTION */}

          <Variant
            setSelected={setSelected}
            selected={selected}
            variants={variants}
            setVariants={setVariants}
            children={
              <div>
                {variants.colour?.custom ? (
                  <Color variants={variants} setVariants={setVariants} />
                ) : null}
              </div>
            }
          />

          <div
            onClick={
              !active() ? () => handleSaveChanges("add-inventory") : () => {}
            }
            className={`border- border-[#E1E6E1] rounded-lg mt-3 flex items-center gap-1 p-3 cursor-pointer bg-white justify-center group ${
              active() ? "opacity-50 cursor-not-allowed" : ""
            }`}
            aria-disabled={active()}
          >
            <AddCircleOutline
              style={{
                fontSize: "20px",
                color: active() ? "#ABB6AC" : "#2B8C34",
              }}
            />
            <p
              className={`font-semibold text-sm leading-[18px] ${
                active() ? "text-[#ABB6AC]" : "text-[#2B8C34]"
              }`}
            >
              Add Inventory
            </p>
          </div>
        </div>

        {/* SECOND SECTION */}
        <div className="w-[40%]">
          {/* STATUS */}
          <div className="relative z-10  rounded-lg bg-white h-fit border border-[#E1E6E1]">
            <div className="flex flex-col">
              <div className="border-b border-b-[#E1E6E1] px-4 py-3">
                <div className="flex items-center gap-1">
                  <p className="text-[#4B564D] font-semibold text-sm">Status</p>
                </div>
              </div>

              <div className="relative w-full px-4 py-3">
                <div
                  onClick={() => setIsStatusOpen(!isStatusOpen)}
                  className="flex justify-between items-center px-[16px] border border-[#96A397] rounded-[8px] h-[44px] w-full cursor-pointer"
                >
                  <p className="font-[500] text-sm leading-[20px] capitalize text-[#344335]">
                    {selectedStatus.name || "Select Status"}
                  </p>
                  <ExpandMore
                    className="text-[#ABB6AC] cursor-pointer"
                    style={{ fontSize: "18px" }}
                  />
                </div>

                {isStatusOpen && (
                  <div className="absolute right-0  w-full rounded-lg  bg-white padding-[8px] mt-[1%] border cursor-pointer py-[8px] mb-[50px] z-20 max-h-[250px] overflow-y-scroll shadow-md">
                    {status?.map((data, index) => {
                      return (
                        <p
                          className="py-[12px] px-[16px] text-sm font-medium capitalize leading-5 hover:bg-[#EDF7EE]"
                          key={index}
                          onClick={() => handleSelectedStatus(data)}
                        >
                          {data.name}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PRODUCT DETAILS */}
          {type === "Bird Repellant" ||
          type === "Storage Product" ||
          type === "Rodenticide" ||
          type === "Nematicide" ? (
            <CropCategoryTwo
              categoryTwoData={categoryTwoData}
              setCategoryTwoData={setCategoryTwoData}
            />
          ) : null}

          {type === "Herbicide" ||
          type === "Insecticide" ||
          type === "Plant Growth Regulator Solution" ||
          type === "Fungicide" ? (
            <CropCategoryOne
              categoryOneData={categoryOneData}
              setCategoryOneData={setCategoryOneData}
            />
          ) : null}

          {type === "Fertilizers" ? (
            <CropCategoryThree
              categoryThreeData={categoryThreeData}
              setCategoryThreeData={setCategoryThreeData}
            />
          ) : null}
          {type === "Seed Dressing" ? (
            <CropCategoryFour
              categoryFourData={categoryFourData}
              setCategoryFourData={setCategoryFourData}
            />
          ) : null}
          {type === "Seed" ? (
            <CropCategoryFive
              categoryFiveData={categoryFiveData}
              setCategoryFiveData={setCategoryFiveData}
            />
          ) : null}
        </div>
      </div>

      {/* PRODCUCT SAVED SUCCESSFULLY */}
      <ModalComponent
        title="Product Saved Successfully"
        show={isOpen}
        showModal={() => setIsOpen(false)}
        subtitle={
          <div className="mt-3 text-sm font-medium text-[#5C715E] ">
            Do you want to add inventory for{" "}
            <span className="font-semibold text-[#344335]"> {productName}</span>{" "}
            now?
          </div>
        }
      >
        <div className="pt-[32px] flex justify-end gap-[40px] items-center mb-[12px]">
          <p
            aria-disabled={loading}
            className="text-sm text-medium leading-5 text-[#5C715E] cursor-pointer"
            onClick={() => (loading ? null : handleSaveChanges("not-now"))}
          >
            {loading ? "Loading....." : "Not Now"}
          </p>
          <button
            disabled={loading}
            className="outline-0 bg-primary rounded-[8px] w-[150px] h-[40px] text-sm text-medium leading-5 text-white disabled:bg-[#ABB6AC] disabled:cursor-not-allowed"
            onClick={() => handleSaveChanges("add-inventory")}
          >
            Yes, Add Inventory
          </button>
        </div>
      </ModalComponent>
    </div>
  );
};

export default AddProduct;
